import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import FieldSelect from "components/field-select";
import { usePublicClassifierValues } from "hooks/api/system-admin/classifiers/use-public-classifier-values";
import { useDictionary } from "hooks/use-dictionary";
import { useUrlParams } from "hooks/use-url-params";
import dayjs from "dayjs";
import { useState } from "react";
import { ClassifierType } from "types/classifier";
import { LicenseStatus } from "types/license";
import { PublicSpecialistLicenseAndStampFilters } from "types/specialist-license-and-stamp";

interface FiltersBarProps {
  onSubmit: (filters: PublicSpecialistLicenseAndStampFilters) => void;
  onClear: () => void;
}

const defaultFilters: PublicSpecialistLicenseAndStampFilters = {
  stampNumber: null,
  firstName: null,
  lastName: null,
  professionalQualificationId: null,
  licenseNumber: null,
  licenseStatus: null,
  licenseIssueDateFrom: null,
  licenseIssueDateTo: null,
};

const FiltersBar = ({ onSubmit, onClear }: FiltersBarProps) => {
  const dict = useDictionary();
  const { filters: urlFilters, setUrlFilters } = useUrlParams();
  const [filters, setFilters] = useState<PublicSpecialistLicenseAndStampFilters>({
    ...defaultFilters,
    ...urlFilters,
  });
  const { items: qualifications, setFilters: setQualificationFilters } = usePublicClassifierValues(
    ClassifierType.SPECIALIST_LICENSE_TYPE
  );

  const updateTextFilter = (name: "stampNumber" | "firstName" | "lastName" | "licenseNumber") => (value: string) => {
    setFilters((previous) => ({ ...previous, [name]: value || null }));
  };

  return (
    <div className="rounded bg-white border p-5 grid sm:grid-cols-2 gap-5">
      <TextField label={dict.specialistLicenses.stampNumber} value={filters.stampNumber ?? ""} onChange={(event) => updateTextFilter("stampNumber")(event.target.value)} />
      <TextField label={dict.specialistLicenses.firstName} value={filters.firstName ?? ""} onChange={(event) => updateTextFilter("firstName")(event.target.value)} />
      <TextField label={dict.specialistLicenses.lastName} value={filters.lastName ?? ""} onChange={(event) => updateTextFilter("lastName")(event.target.value)} />
      <FieldSelect
        label={dict.specialistLicenses.qualification}
        options={qualifications.map((qualification) => ({ value: qualification.id, label: qualification.valueName }))}
        value={filters.professionalQualificationId ?? ""}
        onChange={(value) => setFilters((previous) => ({ ...previous, professionalQualificationId: value || null }))}
        setFilters={setQualificationFilters}
        lazyLoadable
      />
      <TextField label={dict.specialistLicenses.licenseNumber} value={filters.licenseNumber ?? ""} onChange={(event) => updateTextFilter("licenseNumber")(event.target.value)} />
      <FieldSelect
        label={dict.specialistLicenses.licenseStatus}
        options={Object.values(LicenseStatus).map((status) => ({
          value: status,
          label: dict.specialistUserLicenseStatus[status],
        }))}
        value={filters.licenseStatus ?? ""}
        onChange={(value) => setFilters((previous) => ({ ...previous, licenseStatus: (value as LicenseStatus) || null }))}
      />
      <DatePicker
        label={dict.specialistLicenses.licenseIssueDateFrom}
        value={filters.licenseIssueDateFrom ? dayjs(filters.licenseIssueDateFrom) : null}
        format="YYYY-MM-DD"
        onChange={(value) => setFilters((previous) => ({ ...previous, licenseIssueDateFrom: value?.isValid() ? value.format("YYYY-MM-DD") : null }))}
      />
      <DatePicker
        label={dict.specialistLicenses.licenseIssueDateTo}
        value={filters.licenseIssueDateTo ? dayjs(filters.licenseIssueDateTo) : null}
        format="YYYY-MM-DD"
        onChange={(value) => setFilters((previous) => ({ ...previous, licenseIssueDateTo: value?.isValid() ? value.format("YYYY-MM-DD") : null }))}
      />
      <div className="flex gap-3">
        <Button
          type="submit"
          color="primary"
          variant="contained"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
          onClick={() => {
            onSubmit(filters);
            setUrlFilters(filters);
          }}
        >
          {dict.common.search}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          className="sm:w-[172px] flex-1 sm:flex-[unset]"
          onClick={() => {
            onClear();
            setFilters(defaultFilters);
            setUrlFilters(defaultFilters);
          }}
        >
          {dict.common.clear}
        </Button>
      </div>
    </div>
  );
};

export default FiltersBar;
